import { useTranslation } from 'react-i18next';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { SAPTreeView } from '../SAPTreeView';
import { SAPTreeViewOptimized } from '../SAPTreeViewOptimized';
import { SAPTreeViewVirtualized } from '../SAPTreeViewVirtualized';
import LazyMachineGrid from '../LazyMachineGrid';
import GlobeView from '../GlobeView';
import HomeFilterBar from './HomeFilterBar';
import { type Machine, type Line, type Factory } from '../../services/BackendDataService';
import { useNavigate } from 'react-router-dom';
import LineCard from '../LineCard';
import { useMemo, useEffect, useState } from 'react';
import { useThemeClasses } from '../../providers/ThemeProvider';

// Simple cx utility for combining class names
const cx = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

interface AllTabProps {
  viewMode: 'cards' | 'tree' | 'globe';
  setViewMode: (mode: 'cards' | 'tree' | 'globe') => void;
  expandedNodes: Set<string>;
  setExpandedNodes: (nodes: Set<string>) => void;
}

const AllTab = ({ viewMode, setViewMode, expandedNodes, setExpandedNodes }: AllTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeClasses = useThemeClasses();
  
  // Collapsible sections state - persist to localStorage
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('pc-collapsed-sections');
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading collapsed sections:', e);
    }
    return new Set();
  });
  
  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      // Save to localStorage
      try {
        localStorage.setItem('pc-collapsed-sections', JSON.stringify([...newSet]));
      } catch (e) {
        console.error('Error saving collapsed sections:', e);
      }
      return newSet;
    });
  };
  
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // Tailwind's sm breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Get store instance once to avoid multiple subscriptions
  const store = useSAPDataStore();
  const isLoading = useSAPDataStore(state => state.loading);
  const lastFetched = useSAPDataStore(state => state.lastFetched);

  // Use memoized filtered data to prevent infinite loops
  const filteredData = useMemo(() => {
    try {
      const result = {
        sapTreeData: store.getFilteredSAPTreeData(),
        machines: store.getFilteredMachines(),
        lines: store.getFilteredLines(),
        searchTags: store.searchTags,
        searchOperators: store.searchOperators
      };
      
      return result;
    } catch (error) {
      console.error('Error filtering data:', error);
      return {
        sapTreeData: {},
        machines: [],
        lines: [],
        searchTags: [],
        searchOperators: []
      };
    }
  }, [store.searchTags, store.searchOperators, store.showOnlyWithData, store.machines, store.lines, store.sapTreeData]);
  
  const { sapTreeData: filteredSAPTreeData, machines: filteredMachines, lines: filteredLines } = filteredData;

  // Navigation handlers for machines and lines
  const handleLineClick = (line: Line) => {
    navigate(`/line/${line.linieBK}`, {
      state: {
        lineName: line.linieName,
        factory: line.factoryName || '1201 (Factory Name)',
        machineCount: line.maschinen?.length?.toString() || '0',
        lineType: 'Production Line',
        supervisor: 'John Smith'
      }
    });
  };

  // Count total nodes in the tree for performance optimization
  const treeNodeCount = useMemo(() => {
    let count = 0;
    Object.values(filteredSAPTreeData).forEach(group => {
      count += 1; // group
      group.regionen?.forEach(region => {
        count += 1; // region
        region.laender?.forEach(country => {
          count += 1; // country
          country.kunden?.forEach(customer => {
            count += 1; // customer
            customer.werke?.forEach(factory => {
              count += 1; // factory
              factory.linien?.forEach(line => {
                count += 1; // line
                count += line.maschinen?.length || 0; // machines
              });
            });
          });
        });
      });
    });
    return count;
  }, [filteredSAPTreeData]);

  const handleMachineClick = (machine: Machine) => {
    const machineId = machine.maschinennummer || machine.maschineBK;
    const machineName = machine.machineName || machine.bautyp;
    const lineId = machine.lineId || '';
    const machineType = machine.bautyp;
    navigate(`/machine/${machineId}`, {
      state: {
        machineName,
        lineId,
        machineType,
        factory: machine.factoryName || '1201 (Factory Name)'
      }
    });
  };

  const handleSAPFactoryClick = (_factory: Factory) => {
    // No action needed for factory clicks
  };

  // SAPTreeView expanded nodes logic
  const handleTreeToggle = (nodeId: string) => {
    // Ignore special force update nodes
    if (nodeId === '__force_update__') {
      return;
    }
    
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const isExpanded = (nodeId: string) => {
    return expandedNodes.has(nodeId);
  };

  // Helper function to get all possible node IDs from the SAP data
  const getAllNodeIds = () => {
    const ids: string[] = [];
    Object.entries(filteredSAPTreeData).forEach(([groupKey, customerGroup]) => {
      ids.push(`group-${groupKey}`);
      customerGroup.regionen?.forEach((region) => {
        ids.push(`region-${groupKey}-${region.region}`);
        region.laender?.forEach((country) => {
          ids.push(`country-${groupKey}-${region.region}-${country.land}`);
          country.kunden?.forEach((customer) => {
            ids.push(`customer-${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`);
            customer.werke?.forEach((factory) => {
              ids.push(`factory-${customer.kundennummer}-${factory.werkName}`);
              factory.linien?.forEach((line) => {
                ids.push(`line-${factory.werkName}-${line.linieBK}`);
              });
            });
          });
        });
      });
    });
    return ids;
  };

  const handleExpandAll = () => {
    console.log('🌳 AllTab: Expand All clicked');
    const allIds = getAllNodeIds();
    console.log('🌳 AllTab: Expanding all nodes:', allIds.length);
    setExpandedNodes(new Set(allIds));
  };

  const handleCollapseAll = () => {
    console.log('🌳 AllTab: Collapse All clicked');
    console.log('🌳 AllTab: Clearing all expanded nodes');
    setExpandedNodes(new Set());
  };

  // Smart tree component selection based on data size
  const TreeComponent = useMemo(() => {
    if (treeNodeCount < 500) {
      return SAPTreeView; // Original optimized version
    } else if (treeNodeCount < 2000) {
      return SAPTreeViewOptimized; // Flattened structure with search
    } else {
      return SAPTreeViewVirtualized; // Virtualized for large datasets
    }
  }, [treeNodeCount]);

  // Tree component props based on selected component
  const treeProps = useMemo(() => {
    const baseProps = {
      sapData: filteredSAPTreeData,
      onMachineClick: handleMachineClick,
      onLineClick: handleLineClick,
      onFactoryClick: handleSAPFactoryClick,
      expandedNodes,
      onToggleNode: handleTreeToggle,
      isExpanded,
      onExpandAll: handleExpandAll,
      onCollapseAll: handleCollapseAll,
      isMobile // Add mobile detection
    };

    // Add specific props for optimized versions
    if (TreeComponent === SAPTreeViewOptimized) {
      return {
        ...baseProps,
        maxVisibleNodes: 1000 // Reasonable limit for performance
      };
    } else if (TreeComponent === SAPTreeViewVirtualized) {
      return {
        ...baseProps
        // Remove fixed height - let component calculate dynamically
      };
    }
    
    return baseProps;
  }, [filteredSAPTreeData, handleMachineClick, handleLineClick, handleSAPFactoryClick, 
      expandedNodes, handleTreeToggle, isExpanded, handleExpandAll, handleCollapseAll, TreeComponent, isMobile]);

  return (
    <div className="flex flex-col h-full space-y-6">
      {/* View Mode Toggle and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-shrink-0">
        <h3 className={cx(themeClasses.textPrimary, "text-lg sm:text-xl font-semibold")}>{t('allData') || 'All Production Data'}</h3>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className={cx(themeClasses.textSecondary, "text-xs sm:text-sm order-last sm:order-first")}>
            {filteredMachines.length} machines, {filteredLines.length} lines
          </div>
          {/* Data Filter Toggle - now managed by the store */}
          <div className="order-first sm:order-none">
            <HomeFilterBar />
          </div>
          {/* View Mode Toggle */}
          <div className={cx(themeClasses.cardBg, themeClasses.cardBorder, "flex rounded-lg border overflow-hidden w-full sm:w-auto")}>
            <button
              onClick={() => setViewMode('cards')}
              className={cx(
                "flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors",
                viewMode === 'cards' 
                  ? "bg-blue-500 text-white"
                  : cx(themeClasses.textPrimary, themeClasses.hover)
              )}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('tree')}
              className={cx(
                "flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors",
                themeClasses.borderPrimary, "border-l",
                viewMode === 'tree' 
                  ? "bg-blue-500 text-white"
                  : cx(themeClasses.textPrimary, themeClasses.hover)
              )}
            >
              Tree
            </button>
            <button
              onClick={() => setViewMode('globe')}
              className={cx(
                "flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors",
                themeClasses.borderPrimary, "border-l",
                viewMode === 'globe' 
                  ? "bg-blue-500 text-white"
                  : cx(themeClasses.textPrimary, themeClasses.hover)
              )}
            >
              Globe
            </button>
          </div>
        </div>
      </div>
      
      {/* Main content area */}
      <div className="flex-1 min-h-0">
        {/* Loading state: spinner until we get a confirmed response */}
        {(isLoading || !lastFetched) ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[320px] gap-4">
            <div className="w-12 h-12 rounded-full border-4 border-blue-100 dark:border-blue-900 border-t-blue-500 animate-spin" />
            <p className={cx(themeClasses.textSecondary, "text-sm")}>
              {t('loadingMachines') || 'Loading machines...'}
            </p>
          </div>
        ) : viewMode === 'tree' ? (
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0">
              <TreeComponent {...treeProps} />
            </div>
          </div>
        ) : viewMode === 'globe' ? (
          <div className="h-full">
            <GlobeView
              sapData={filteredSAPTreeData}
              onMachineClick={handleMachineClick}
            />
          </div>
        ) : (
          // Cards View - unified data display
          <div className="space-y-6 sm:space-y-8 overflow-y-auto h-full px-1 sm:px-0">
          {/* Lines Section */}
          <section className="space-y-3 sm:space-y-4">
            <button
              onClick={() => toggleSection('lines')}
              className={cx(
                themeClasses.textPrimary,
                "flex items-center gap-2 text-lg sm:text-xl font-semibold px-2 sm:px-0 hover:opacity-80 transition-opacity w-full text-left"
              )}
            >
              <svg
                className={cx(
                  "w-5 h-5 transition-transform duration-200",
                  collapsedSections.has('lines') ? "" : "rotate-90"
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {t('productionLines') || 'Production Lines'}
              <span className={cx(themeClasses.textSecondary, "text-sm font-normal")}>({filteredLines.length})</span>
            </button>
            {!collapsedSections.has('lines') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 px-2 sm:px-0">
                {filteredLines.map((line, index) => {
                  // Create a unique key that's guaranteed to be defined and unique
                  const lineId = line.linieBK || line.linieName || `unknown-line`;
                  const searchContext = filteredData.searchTags.length > 0 ? `-search-${filteredData.searchTags.join('-')}` : '';
                  const uniqueKey = `line-${index}-${lineId}${searchContext}`;
                  
                  return (
                    <LineCard
                      key={uniqueKey}
                      line={line}
                      onClick={handleLineClick}
                    />
                  );
                })}
              </div>
            )}
          </section>
          {/* Machines Section */}
          <section className="space-y-3 sm:space-y-4">
            <button
              onClick={() => toggleSection('machines')}
              className={cx(
                themeClasses.textPrimary,
                "flex items-center gap-2 text-lg sm:text-xl font-semibold px-2 sm:px-0 hover:opacity-80 transition-opacity w-full text-left"
              )}
            >
              <svg
                className={cx(
                  "w-5 h-5 transition-transform duration-200",
                  collapsedSections.has('machines') ? "" : "rotate-90"
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {t('allMachines') || 'All Machines'}
              <span className={cx(themeClasses.textSecondary, "text-sm font-normal")}>({filteredMachines.length})</span>
            </button>
            {!collapsedSections.has('machines') && (
              <div className="px-2 sm:px-0">
                <LazyMachineGrid
                  key={`machines-${filteredData.searchTags.join('-')}`}
                  machines={filteredMachines}
                  onMachineClick={handleMachineClick}
                  initialLoadCount={12}
                  loadMoreCount={12}
                />
              </div>
            )}
          </section>
          
        </div>
        )}
      </div>
    </div>
  );
};

export default AllTab;